import {
  streamerHistorySchema,
  type HistoryMonth,
  type StreamerHistory,
} from '@growup/streaming-shared';
import { Table, type Column } from '@growup/ui';
import { useEffect, useState } from 'react';

import { getJson, messageOf } from './api';
import { formatChange, formatCount, formatDate, formatMinutes } from './format';
import { Card, ErrorNote } from './ui';

const monthFormat = new Intl.DateTimeFormat('pt-PT', { timeZone: 'UTC', month: 'short' });

/** `2026-03` → `mar. 2026`. */
export function formatMonth(month: string): string {
  return `${monthFormat.format(new Date(`${month}-15T12:00:00Z`))} ${month.slice(0, 4)}`;
}

const SOURCE_LABEL: Record<HistoryMonth['source'], string> = {
  sullygnome: 'SullyGnome',
  twitch: 'Twitch',
};

const MONTH_COLUMNS: Column<HistoryMonth>[] = [
  { key: 'month', header: 'Mês', cell: (m) => formatMonth(m.month) },
  {
    key: 'live',
    header: 'Em direto',
    align: 'right',
    cell: (m) => (m.hasData ? formatMinutes(m.minutesLive) : 'sem dados'),
  },
  {
    key: 'streams',
    header: 'Transmissões',
    align: 'right',
    hideOnMobile: true,
    cell: (m) => (m.hasData ? formatCount(m.streams) : '—'),
  },
  {
    key: 'viewers',
    header: 'Média · pico',
    align: 'right',
    hideOnMobile: true,
    cell: (m) => (m.hasData ? `${formatCount(m.avgViewers)} · ${formatCount(m.peakViewers)}` : '—'),
  },
  {
    key: 'followers',
    header: 'Seguidores',
    align: 'right',
    cell: (m) => (m.followersGained === null ? '—' : formatChange(m.followersGained)),
  },
  { key: 'source', header: 'Fonte', hideOnMobile: true, cell: (m) => SOURCE_LABEL[m.source] },
];

/**
 * Historico mes a mes no separador Numeros: os meses antigos vem da recolha
 * unica no SullyGnome; os seguintes, das nossas leituras da Twitch.
 */
export function HistoryCard() {
  const [data, setData] = useState<StreamerHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson('/api/me/history', streamerHistorySchema)
      .then(setData)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  if (error) return <ErrorNote message={error} />;
  if (data === null || data.months.length === 0) return null;
  const year = data.ranges.find((r) => r.days === 365 && r.hasData);
  return (
    <Card title="Mês a mês">
      <div className="flex flex-col gap-4">
        <MonthBars months={data.months} />
        {year && data.importedAt ? (
          <p className="text-caption text-gu-ink-muted">
            Nos 365 dias até {formatDate(data.importedAt)} (SullyGnome):{' '}
            {formatMinutes(year.minutesLive)} em direto, {formatMinutes(year.watchMinutes)} vistas,{' '}
            {formatChange(year.followersGained)} seguidores.
          </p>
        ) : null}
        <Table
          caption="Histórico mês a mês"
          columns={MONTH_COLUMNS}
          rows={[...data.months].reverse()}
          rowKey={(m) => m.month}
          empty="Sem histórico."
        />
        {data.months.some((m) => m.source === 'sullygnome') ? (
          <p className="text-caption text-gu-ink-muted">
            Os meses do SullyGnome vêm de leituras de 15 em 15 minutos e não batem certo ao
            espectador com as estatísticas da Twitch. "Sem dados": o SullyGnome não tinha o canal
            nesse mês.
          </p>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Uma barra por mes (horas em direto), da cor da marca; as do SullyGnome mais
 * claras. Cada barra tem o seu rotulo; a tabela por baixo e a vista em texto.
 */
export function MonthBars({ months }: { months: HistoryMonth[] }) {
  const max = Math.max(1, ...months.map((m) => m.minutesLive));
  const both =
    months.some((m) => m.source === 'sullygnome') && months.some((m) => m.source === 'twitch');
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-40 items-end gap-[2px] border-b border-gu-line" role="list">
        {months.map((m) => {
          const label = `${formatMonth(m.month)}: ${
            m.hasData ? formatMinutes(m.minutesLive) : 'sem dados'
          } (${SOURCE_LABEL[m.source]})`;
          return (
            <div
              key={m.month}
              role="listitem"
              aria-label={label}
              title={label}
              className="group flex h-full flex-1 items-end hover:bg-gu-ink/5"
            >
              {m.hasData ? (
                <div
                  className={`w-full rounded-t bg-gu-accent group-hover:opacity-80 ${
                    m.source === 'sullygnome' ? 'opacity-50' : ''
                  }`}
                  style={{ height: `${String((m.minutesLive / max) * 100)}%` }}
                />
              ) : (
                <div className="w-full border-t-2 border-dashed border-gu-line" />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-caption text-gu-ink-muted">
        <span>{formatMonth(months[0]?.month ?? '')}</span>
        <span>máx. {formatMinutes(max)}</span>
        <span>{formatMonth(months[months.length - 1]?.month ?? '')}</span>
      </div>
      {both ? (
        <p className="flex flex-wrap gap-4 text-caption text-gu-ink-muted">
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded-sm bg-gu-accent opacity-50" /> SullyGnome
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded-sm bg-gu-accent" /> Twitch (Grow uP)
          </span>
        </p>
      ) : null}
    </div>
  );
}
